import { Ability, AbilityBuilder } from '@casl/ability';
import { differenceInMilliseconds, parseISO, isValid, toDate, format as format$1, startOfDay, formatDistanceStrict } from 'date-fns';
import { utcToZonedTime, format } from 'date-fns-tz';
import { isString, isArray, transform, isEqual, isObject, mergeWith, omit, each, map, omitBy, toNumber as toNumber$1, isNaN, isEmpty, isNumber, isBoolean, isDate, keys } from 'lodash';

const AppAbility = Ability;
function CreateRoot() {
  const { can, build } = new AbilityBuilder(Ability);
  can("manage", "all");
  return build();
}
function CreateForUser(user, clientId, root) {
  const { can, build } = new AbilityBuilder(Ability);
  if (root) {
    return CreateRoot();
  }
  const kcClientId = clientId ? clientId : process.env["KEYCLOAK_CLIENT_ID"];
  if (user.resource_access && (kcClientId || "glx-lazarus") in user.resource_access) {
    const clientRoles = user.resource_access[kcClientId || "glx-lazarus"];
    if (clientRoles) {
      clientRoles.roles.forEach((role) => {
        const [entity, access] = role.split("::");
        can(access.toLowerCase(), entity.toLowerCase());
      });
    }
  }
  return build();
}

function timeUntilExpiry(expiryTime) {
  if (expiryTime) {
    const timeRemaining = Math.max(
      0,
      differenceInMilliseconds(new Date(expiryTime * 1e3), /* @__PURE__ */ new Date())
    );
    return timeRemaining;
  }
  return 0;
}

const API_FORMAT_DATE_ISO = "yyyy-MM-dd'T'HH:mm:ss.SSSXXX";
const FORMAT_DATE = "dd/MM/yyyy";
const FORMAT_DATE_TIME = "dd/MM/yyyy h:mm a";
const FORMAT_DATE_TIME_TIMEZONE = "dd/MM/yyyy h:mm a zzz";
const FORMAT_DATE_TIME_SECONDS = "dd/MM/yyyy h:mm:ss a";
const FORMAT_DATE_TIME_SECONDS_TIMEZONE = "dd/MM/yyyy h:mm:ss a zzz";
const FORMAT_MONTH_YEAR = "MMMM yyyy";
const FORMAT_MONTH_YEAR_SHORT = "MMM yyyy";
const FORMAT_MONTH_SHORT = "MMM";
const FORMAT_MONTH_LONG = "MMMM";
const FORMAT_DAY_MONTH_SHORT = "dd MMM";
const FORMAT_DAY_MONTH_YEAR_SHORT = "dd MMM yyyy";
const FORMAT_DAY_OF_MONTH_YEAR_SHORT = "do MMM, yyyy";
const FORMAT_TIME = "h:mm a";
const FORMAT_TIME_SECONDS = "h:mm:ss a";
const A_DAY = 3600 * 24;
const AN_HOUR = 3600;
const A_MINUTE = 60;

const convertDateToTimezone = (date, timeZone) => {
  if (timeZone) {
    return utcToZonedTime(date, timeZone);
  }
  return date;
};
const overrideTimezone = (date, timezone) => {
  if (!timezone) {
    return date;
  }
  const converted = format(date, API_FORMAT_DATE_ISO, {
    timeZone: timezone
  });
  return getDate(converted);
};
const getDate = (date) => {
  let dateObj = null;
  if (isString(date)) {
    dateObj = parseISO(date);
  } else if (isValid(date)) {
    dateObj = toDate(date);
  }
  if (dateObj && isValid(dateObj)) {
    return dateObj;
  }
  return void 0;
};
const formatDateAs = (date, options = {}) => {
  const { dateFormat = FORMAT_DATE, timeZone, fallback = "" } = options;
  const dateObj = getDate(date);
  if (timeZone) {
    return dateObj ? format(convertDateToTimezone(dateObj, timeZone), dateFormat, {
      timeZone
    }) : fallback;
  }
  return dateObj ? format$1(dateObj, dateFormat) : fallback;
};
const formatDate = (date, options = {}) => {
  const combinedOptions = {
    dateFormat: FORMAT_DATE,
    ...options
  };
  return formatDateAs(date, combinedOptions);
};
const formatDateTime = (date, options = {}) => {
  const { includeSeconds = false, includeTimezone = false, ...standardOptions } = options;
  let defaultFormat = FORMAT_DATE_TIME;
  if (includeTimezone) {
    defaultFormat = includeSeconds ? FORMAT_DATE_TIME_SECONDS_TIMEZONE : FORMAT_DATE_TIME_TIMEZONE;
  } else if (includeSeconds) {
    defaultFormat = FORMAT_DATE_TIME_SECONDS;
  }
  const combinedOptions = {
    dateFormat: defaultFormat,
    ...standardOptions
  };
  return formatDateAs(date, combinedOptions);
};
const formatISODate = formatDate;
const formatISODateTime = formatDateTime;
const formatDateForAPI = (date, options = {}) => {
  const combinedOptions = {
    dateFormat: API_FORMAT_DATE_ISO,
    ...options
  };
  return date ? formatDateAs(startOfDay(date), combinedOptions) : "";
};
const formatDateTimeForAPI = (date, options = {}) => {
  const combinedOptions = {
    dateFormat: API_FORMAT_DATE_ISO,
    ...options
  };
  return date ? formatDateAs(date, combinedOptions) : "";
};
const getFriendlyDateDistance = (timestamp) => {
  const timestampDate = getDate(timestamp);
  return timestampDate ? formatDistanceStrict(timestampDate, /* @__PURE__ */ new Date(), {
    addSuffix: true
  }) : "";
};
function getDaysHoursMinutes(duration = 0) {
  const days = Math.floor(duration / A_DAY);
  const hours = Math.floor(duration % A_DAY / AN_HOUR);
  const min = Math.floor(duration % AN_HOUR / A_MINUTE);
  const minutes = min < 59 ? min : 59;
  return {
    days,
    hours,
    minutes
  };
}
function getDaysHoursMinutesInWords(duration = 0) {
  const { days, hours, minutes } = getDaysHoursMinutes(duration);
  return [
    days == 0 ? null : days > 1 ? `${days} days` : `${days} day`,
    hours == 0 ? null : hours > 1 ? `${hours} hours` : `${hours} hour`,
    minutes == 0 ? null : minutes > 1 ? `${minutes} minutes` : `${minutes} minute`
  ].filter((u) => u != null).join(" ");
}
function getTotalSeconds({ days, hours, minutes }) {
  return days * A_DAY + hours * AN_HOUR + minutes * A_MINUTE;
}

function templateReplace(template, replacements) {
  let key;
  for (key in replacements) {
    template = template.replace(
      new RegExp("\\{" + key + "\\}", "gm"),
      replacements[key]?.toString() ?? ""
    );
  }
  return template;
}

function asArray(obj) {
  return isArray(obj) ? obj : [obj];
}

function deepDiff(object, base) {
  function changes(object2, base2) {
    return transform(object2, function(result, value, key) {
      if (!isEqual(value, base2[key])) {
        result[key] = isObject(value) && isObject(base2[key]) ? changes(value, base2[key]) : value;
      }
    });
  }
  return changes(object, base);
}

function mergeWithArrayOverwrite(destination, source) {
  return mergeWith(
    destination,
    source,
    (destinationValue, sourceValue) => isArray(sourceValue) ? sourceValue : void 0
  );
}

function omitDeep(obj, paths) {
  const newObject = isObject(obj) && !isArray(obj) ? omit(obj, paths) : obj;
  each(newObject, function(val, key) {
    if (isArray(val)) {
      newObject[key] = map(val, (arrayValue) => omitDeep(arrayValue, paths));
    } else if (isObject(val)) {
      newObject[key] = omitDeep(val, paths);
    }
  });
  return newObject;
}
function omitDeepBy(obj, predicate) {
  const newObject = isObject(obj) && !isArray(obj) ? omitBy(obj, predicate) : obj;
  each(newObject, function(val, key) {
    if (isArray(val)) {
      newObject[key] = map(val, (arrayValue) => omitDeepBy(arrayValue, predicate));
    } else if (isObject(val)) {
      newObject[key] = omitDeepBy(val, predicate);
    }
  });
  return newObject;
}

function toNumber(input, fallback) {
  const output = toNumber$1(input);
  if (isNaN(output)) {
    return fallback;
  }
  return output;
}

function haveInputValue(value) {
  return !isEmpty(value) || isNumber(value) || isBoolean(value) || isDate(value);
}

const defaultConfig = {
  clientSettings: {
    title: "GLX Connect",
    logoPath: "https://glx-customer-assets.s3.ap-southeast-2.amazonaws.com/glx/logo-glx.svg",
    faviconPath: "https://glx-customer-assets.s3.ap-southeast-2.amazonaws.com/glx/favicon-glx.svg"
  },
  keycloakSettings: {
    locale: {
      en: {
        loginPageBackgroundUrl: "url(https://uploads-ssl.webflow.com/60593a13f41fc73436454f8e/607d2b677c67771d48e58cef_iStock-840815750.jpg)",
        loginCardBackgroundUrl: "url(https://uploads-ssl.webflow.com/60593a13f41fc73436454f8e/612335d4ad4d0f76ed89833f_background_wave.png)",
        loginCardLogoUrl: "url(https://uploads-ssl.webflow.com/60593a13f41fc73436454f8e/60627d6836f3df3860fdd864_logo.svg)",
        loginCardAccentColor: "#D71638"
        //customFooter: "'{\"preText\":\"By continuing, you agree to:\",\"items\":[{\"text\":\"Document One\",\"url\":\"https://google.com\"},{\"text\":\"Document Two\",\"url\":\"https://google.com\"}]}'"
      }
    }
  },
  notificationSettings: {
    enabled: true,
    emailFrom: "support@glxdigital.com",
    smsFrom: "GLXConnect"
  },
  tradeSettings: {
    tenders: {
      defaultConsideration: 604800
      // 1 week
    },
    bilaterals: {
      defaultEnd: 2592e3
      // 30 days
    },
    dealConfirmations: {
      defaultEnd: 2592e3
      // 30 days
    }
  }
};

var FeatureFlags = /* @__PURE__ */ ((FeatureFlags2) => {
  FeatureFlags2["Auctions"] = "auctions";
  FeatureFlags2["ReviewTermSheet"] = "reviewtermsheet";
  FeatureFlags2["DealConfirmation"] = "dealconfirmation";
  FeatureFlags2["ChatAlias"] = "chatalias";
  FeatureFlags2["AVScan"] = "avscan";
  return FeatureFlags2;
})(FeatureFlags || {});
const defaultFlags = {
  ["auctions" /* Auctions */]: false,
  ["reviewtermsheet" /* ReviewTermSheet */]: false,
  ["dealconfirmation" /* DealConfirmation */]: false,
  ["chatalias" /* ChatAlias */]: false,
  ["avscan" /* AVScan */]: false
};
const availableFlags = keys(defaultFlags);

export { AN_HOUR, API_FORMAT_DATE_ISO, A_DAY, A_MINUTE, AppAbility, CreateForUser, CreateRoot, FORMAT_DATE, FORMAT_DATE_TIME, FORMAT_DATE_TIME_SECONDS, FORMAT_DATE_TIME_SECONDS_TIMEZONE, FORMAT_DATE_TIME_TIMEZONE, FORMAT_DAY_MONTH_SHORT, FORMAT_DAY_MONTH_YEAR_SHORT, FORMAT_DAY_OF_MONTH_YEAR_SHORT, FORMAT_MONTH_LONG, FORMAT_MONTH_SHORT, FORMAT_MONTH_YEAR, FORMAT_MONTH_YEAR_SHORT, FORMAT_TIME, FORMAT_TIME_SECONDS, FeatureFlags, asArray, availableFlags, convertDateToTimezone, deepDiff, defaultConfig, defaultFlags, formatDate, formatDateAs, formatDateForAPI, formatDateTime, formatDateTimeForAPI, formatISODate, formatISODateTime, getDate, getDaysHoursMinutes, getDaysHoursMinutesInWords, getFriendlyDateDistance, getTotalSeconds, haveInputValue, mergeWithArrayOverwrite, omitDeep, omitDeepBy, overrideTimezone, templateReplace, timeUntilExpiry, toNumber };
//# sourceMappingURL=index.esm.js.map
